import { Suspense, useState, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { bootstrapSession } from '@/lib/bootstrap-session'
import { connectNotificationSocket, isNotificationSocketConnected } from '@/lib/notification-ws'
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone'
import { useCompanyCurrency } from '@/hooks/useCompanyCurrency'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { PageTransition } from './PageTransition'
import { Skeleton } from '@/components/ui/skeleton'
import { CallOverlay } from '@/features/inbox/calls/CallOverlay'

function ContentLoader() {
  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <Skeleton className="h-8 w-64 max-w-full" />
      <Skeleton className="h-4 w-96 max-w-full" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
      <Skeleton className="h-64 mt-4 rounded-2xl" />
    </div>
  )
}

export function AppLayout() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const location = useLocation()
  const pageKey = location.pathname.split('/')[1] || 'root'
  // App-like screens (chat, builders, help) fill the viewport themselves, so
  // the page footer is left off there.
  const isFullHeightPage =
    /^\/(inbox|help)(\/|$)/.test(location.pathname) ||
    /\/builder(\/|$)/.test(location.pathname)
  const { isAuthenticated, accessToken } = useAuthStore()

  // The mobile nav drawer closes whenever the route changes (a nav tap, a
  // link inside the page, browser back) so it never lingers over new content.
  // The scrolling <main> outlives route changes, so it is also reset to the
  // top — otherwise a new page opens wherever the previous one was scrolled.
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => {
    setMobileSidebarOpen(false)
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [location.pathname])

  // Escape closes the drawer; while open, the page behind it doesn't scroll.
  useEffect(() => {
    if (!mobileSidebarOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSidebarOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileSidebarOpen])
  useCompanyTimezone()
  useCompanyCurrency()

  // On mount (including page refresh): reconnect WS + bootstrap session once.
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    if (!isNotificationSocketConnected()) {
      connectNotificationSocket(accessToken)
    }

    bootstrapSession().catch(() => {
      // Silently fail — persisted store values remain
    })
  }, [isAuthenticated, accessToken])

  return (
    <div className="flex h-screen overflow-hidden bg-background supports-[height:100dvh]:h-dvh">
      <Sidebar
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Mobile overlay */}
      <div
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 lg:hidden',
          mobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={() => setMobileSidebarOpen(false)}
      />

      {/* Main column */}
      <div className="flex flex-1 flex-col min-w-0">
        <Header onMenuClick={() => setMobileSidebarOpen(true)} />

        {/* App-like screens get a fixed-height region (they scroll inside
            themselves and size to h-full); every other page scrolls here. */}
        <main
          ref={mainRef}
          className={cn(
            'flex flex-1 flex-col',
            isFullHeightPage ? 'min-h-0 overflow-hidden' : 'overflow-y-auto overflow-x-hidden',
          )}
        >
          {/* Full-bleed page container with the standard gutters */}
          <div
            className={cn(
              'w-full min-w-0 flex-1 px-[16px] py-6 sm:px-6 md:px-8 md:py-8',
              isFullHeightPage && 'min-h-0',
            )}
          >
            {/* Suspense MUST sit inside PageTransition. If the motion wrapper
                is a *child* of Suspense, a still-loading lazy chunk hides the
                already-mounted motion.div; framer-motion's rAF-driven fade-in
                is throttled while hidden, so opacity stays at its initial 0 and
                the page renders invisibly (blank until you navigate again).
                Keeping the wrapper outside Suspense lets it animate the skeleton
                in, then swap real content in place — always visible. */}
            <PageTransition key={pageKey} className={isFullHeightPage ? 'h-full' : undefined}>
              <Suspense fallback={<ContentLoader />}>
                <Outlet />
              </Suspense>
            </PageTransition>
          </div>
          {!isFullHeightPage && (
            <footer className="flex min-h-16 shrink-0 items-center justify-between px-[16px] py-3 sm:px-6 md:px-8">
              <span>
                Copyright &copy; {new Date().getFullYear()}{' '}
                <span className="font-semibold">Setu</span> All rights reserved.
              </span>
            </footer>
          )}
        </main>
      </div>

      {/* WhatsApp calls ring on every page, not just the inbox */}
      <CallOverlay />
    </div>
  )
}
